import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr as trLocale } from 'date-fns/locale';
import ContentCardIconSelect, { ContentCardEmojiValue, ContentCardPresetIcon } from './ContentCardIconSelect';
import ContentCardsLayoutPreview from './ContentCardsLayoutPreview';
import {
  formatBannerPercentLabel,
  normalizeBannerPercent,
  resolveBannerDateLabel,
  toYmd,
  ymdToDate
} from './bannerCardHelpers';
import {
  applyLayoutTemplateToSettings,
  MAX_CONTENT_CARDS,
  normalizeContentCardsLayout
} from './contentCardLayoutTemplates';
import {
  BANNER_PANEL_COLORS,
  CONTENT_CARD_ICON_SIZE_PX,
  DEFAULT_CARD_TAG_COLORS,
  DEFAULT_CONTENT_CARD,
  DEFAULT_CONTENT_CARDS_SETTINGS,
  type ContentCardAlign,
  type ContentCardBadgePosition,
  type ContentCardIconSize,
  type ContentCardImageSide,
  type ContentCardItem,
  type ContentCardsDialogState,
  type ContentCardsLayoutTemplate,
  type ContentCardsPageOption,
  type ContentCardsTargetPage
} from './types';

const PROMO_LAYOUTS: ContentCardsLayoutTemplate[] = [
  'promo-trio',
  'promo-large-left',
  'promo-large-right'
];

const resolveCardAlign = (card: ContentCardItem, fallback: ContentCardAlign): ContentCardAlign => {
  const raw = String(card.align || '').trim();
  if (raw === 'left' || raw === 'center' || raw === 'right') return raw;
  return fallback;
};

const resolveTagSoftStyle = (color?: string): { bgcolor: string; color: string } => {
  const raw = String(color || '').trim();
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw)) {
    return { bgcolor: '#e8eefc', color: '#1e3a8a' };
  }
  let hex = raw.slice(1);
  if (hex.length === 3) hex = hex.split('').map((c) => `${c}${c}`).join('');
  const n = parseInt(hex, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return { bgcolor: `rgba(${r}, ${g}, ${b}, 0.14)`, color: `#${hex}` };
};

interface ContentCardsDialogProps {
  state: ContentCardsDialogState;
  onClose: () => void;
  onSave: () => void;
  onStateChange: (updater: (prev: ContentCardsDialogState) => ContentCardsDialogState) => void;
  onOpenImageLibrary: (cardIndex: number, field?: 'imageUrl' | 'brandLogoUrl') => void;
  onTargetPageChange?: (targetPage: ContentCardsTargetPage) => void;
  pageTargetOptions?: ContentCardsPageOption[];
  positionOptions?: Array<{ value: string; label: string }>;
  /** Ana sayfa dışı hedefler için konum seçenekleri (kart/etiket altına vb.) */
  pagePositionOptions?: Array<{ value: string; label: string }>;
  hidePositionSelector?: boolean;
  /** Pazarlama promo: sade banner editörü */
  simpleMode?: boolean;
}

const ContentCardsDialog: React.FC<ContentCardsDialogProps> = ({
  state,
  onClose,
  onSave,
  onStateChange,
  onOpenImageLibrary,
  onTargetPageChange,
  pageTargetOptions = [],
  positionOptions = [],
  pagePositionOptions = [],
  hidePositionSelector = false,
  simpleMode = false
}) => {
  const updateSettings = (patch: Partial<ContentCardsDialogState['settings']>) => {
    onStateChange((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...patch }
    }));
  };

  const updateCard = (index: number, patch: Partial<ContentCardItem>) => {
    onStateChange((prev) => ({
      ...prev,
      cards: prev.cards.map((card, idx) => (idx === index ? { ...card, ...patch } : card))
    }));
  };

  const updateBulletItem = (cardIndex: number, bulletIndex: number, value: string) => {
    onStateChange((prev) => ({
      ...prev,
      cards: prev.cards.map((card, idx) => {
        if (idx !== cardIndex) return card;
        const bullets = [...(card.bulletItems || [])];
        bullets[bulletIndex] = value;
        return { ...card, bulletItems: bullets };
      })
    }));
  };

  const addBulletItem = (cardIndex: number) => {
    onStateChange((prev) => ({
      ...prev,
      cards: prev.cards.map((card, idx) =>
        idx === cardIndex ? { ...card, bulletItems: [...(card.bulletItems || []), ''] } : card
      )
    }));
  };

  const removeBulletItem = (cardIndex: number, bulletIndex: number) => {
    onStateChange((prev) => ({
      ...prev,
      cards: prev.cards.map((card, idx) => {
        if (idx !== cardIndex) return card;
        const bullets = (card.bulletItems || []).filter((_, i) => i !== bulletIndex);
        return { ...card, bulletItems: bullets };
      })
    }));
  };

  const addCard = () => {
    if (state.cards.length >= MAX_CONTENT_CARDS) return;
    onStateChange((prev) => {
      const nextColor = DEFAULT_CARD_TAG_COLORS[prev.cards.length % DEFAULT_CARD_TAG_COLORS.length];
      return {
        ...prev,
        cards: [
          ...prev.cards,
          {
            ...DEFAULT_CONTENT_CARD,
            badgeColor: nextColor,
            align: prev.settings.titleAlign || 'left'
          }
        ]
      };
    });
  };

  const removeCard = (index: number) => {
    onStateChange((prev) => ({
      ...prev,
      cards: prev.cards.length <= 1 ? prev.cards : prev.cards.filter((_, idx) => idx !== index)
    }));
  };

  const handleLayoutChange = (layoutTemplate: ContentCardsLayoutTemplate) => {
    onStateChange((prev) => ({
      ...prev,
      settings: applyLayoutTemplateToSettings(layoutTemplate, prev.settings)
    }));
  };

  const { settings } = state;
  const variant = settings.variant;
  const layoutTemplate = normalizeContentCardsLayout(settings.layoutTemplate);
  const isHomepageTarget = state.targetPage === 'homepage';
  const isBanner = variant === 'banner';
  const isFeatureSplit = variant === 'feature-split';
  const isEmoji = variant === 'emoji';
  const isPricingTrio = variant === 'pricing-trio';
  const isPricingScroll = variant === 'pricing-scroll';
  const isPricing = isPricingTrio || isPricingScroll;
  const showSmallIconField = !simpleMode && !isBanner && !isFeatureSplit && !isPricing;
  const showLargeImageField = !isPricing && !isEmoji;
  const showColumnControls =
    !simpleMode &&
    !isFeatureSplit &&
    !isPricing &&
    (layoutTemplate === 'grid' || layoutTemplate === 'featured' || layoutTemplate === 'custom');
  const isPromoLayout =
    layoutTemplate === 'promo-trio' ||
    layoutTemplate === 'promo-large-left' ||
    layoutTemplate === 'promo-large-right';
  const desktopCols = Math.min(4, Math.max(1, Number(settings.columnsDesktop) || 3));
  const previewAlign = settings.gridAlign || 'center';
  const groupedPageOptions = pageTargetOptions.reduce<Record<string, ContentCardsPageOption[]>>((acc, option) => {
    if (!acc[option.group]) acc[option.group] = [];
    acc[option.group].push(option);
    return acc;
  }, {});

  // İlk dropdown: ana bölümler (Ana Sayfa, Hakkımızda, Hizmetler, ...)
  const mainSectionNames = Object.keys(groupedPageOptions);
  // Seçili hedefin ait olduğu ana bölüm
  const currentTargetOption = pageTargetOptions.find((option) => option.value === state.targetPage);
  const selectedMainSection = currentTargetOption?.group || mainSectionNames[0] || '';
  // İkinci dropdown: seçili ana bölüme ait alt sayfalar (dinamik)
  const subPageOptions = groupedPageOptions[selectedMainSection] || [];

  const handleMainSectionChange = (group: string) => {
    const firstOption = (groupedPageOptions[group] || [])[0];
    if (!firstOption) return;
    const nextTarget = firstOption.value;
    onStateChange((prev) => ({ ...prev, targetPage: nextTarget }));
    onTargetPageChange?.(nextTarget);
  };

  return (
    <Dialog open={state.open} onClose={onClose} maxWidth={simpleMode ? 'md' : 'lg'} fullWidth>
      <DialogTitle>
        {simpleMode
          ? (state.isEdit ? 'Kampanya Kartlarını Düzenle' : 'Kampanya Kartı Oluştur')
          : (state.isEdit ? 'İçerik Kartları Düzenle' : 'İçerik Kartları Ekle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {simpleMode
            ? 'Her kart: sol tarafa görsel, sağ tarafa renk + başlık + CTA. En fazla 3 kart yan yana önerilir.'
            : 'Yerleşim şablonu ile kartları sütun mantığında dizebilirsiniz (ör. 3 yan yana promo, büyük + 2 küçük). Her bloğun sayfası ve sayfadaki konumu ayrı ayrı seçilir.'}
        </DialogContentText>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Yerleşim
        </Typography>
        <Box sx={{ mb: 2.5 }}>
          <ContentCardsLayoutPreview
            value={layoutTemplate}
            onChange={handleLayoutChange}
            allowedLayouts={simpleMode ? PROMO_LAYOUTS : undefined}
          />
        </Box>

        {!simpleMode && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Ana bölüm</InputLabel>
              <Select
                label="Ana bölüm"
                value={selectedMainSection}
                onChange={(e) => handleMainSectionChange(e.target.value as string)}
              >
                {mainSectionNames.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" disabled={subPageOptions.length <= 1}>
              <InputLabel>Alt sayfa</InputLabel>
              <Select
                label="Alt sayfa"
                value={state.targetPage}
                onChange={(e) => {
                  const nextTarget = e.target.value as ContentCardsTargetPage;
                  onStateChange((prev) => ({ ...prev, targetPage: nextTarget }));
                  onTargetPageChange?.(nextTarget);
                }}
              >
                {subPageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {isHomepageTarget && !hidePositionSelector && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Bu bloğun ana sayfadaki konumu</InputLabel>
                <Select
                  label="Bu bloğun ana sayfadaki konumu"
                  value={state.insertAfter}
                  onChange={(e) =>
                    onStateChange((prev) => ({
                      ...prev,
                      insertAfter: e.target.value as ContentCardsDialogState['insertAfter']
                    }))
                  }
                >
                  {positionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {!isHomepageTarget && !hidePositionSelector && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Bu bloğun sayfadaki konumu</InputLabel>
                <Select
                  label="Bu bloğun sayfadaki konumu"
                  value={
                    pagePositionOptions.some((option) => option.value === state.insertAfter)
                      ? state.insertAfter
                      : 'content-end'
                  }
                  onChange={(e) =>
                    onStateChange((prev) => ({
                      ...prev,
                      insertAfter: e.target.value as ContentCardsDialogState['insertAfter']
                    }))
                  }
                >
                  {(pagePositionOptions.length > 0
                    ? pagePositionOptions
                    : [
                        { value: 'content-start', label: 'Sayfa içeriğinin üstünde' },
                        { value: 'content-end', label: 'Sayfa içeriğinin altında' }
                      ]
                  ).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        )}

        {simpleMode && isHomepageTarget && !hidePositionSelector && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Ana sayfadaki konum</InputLabel>
            <Select
              label="Ana sayfadaki konum"
              value={state.insertAfter}
              onChange={(e) =>
                onStateChange((prev) => ({
                  ...prev,
                  insertAfter: e.target.value as ContentCardsDialogState['insertAfter']
                }))
              }
            >
              {positionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          label="Bölüm Başlığı (Opsiyonel)"
          placeholder={simpleMode ? 'Örn. Kampanyalar' : 'Örn. Hizmetlerimiz'}
          value={state.title}
          onChange={(e) => onStateChange((prev) => ({ ...prev, title: e.target.value }))}
          sx={{ mb: 2 }}
        />

        {!simpleMode && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, borderRadius: 1, bgcolor: '#f8fafc' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.showBullets}
                onChange={(e) => updateSettings({ showBullets: e.target.checked })}
              />
            }
            label="Maddelendirme göster"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.showLocation}
                onChange={(e) => updateSettings({ showLocation: e.target.checked })}
              />
            }
            label="Konum göster"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.showDate}
                onChange={(e) => updateSettings({ showDate: e.target.checked })}
              />
            }
            label="Tarih göster"
          />
        </Box>
        )}

        {!simpleMode && settings.showBullets && (
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">İçerik Sırası</FormLabel>
            <RadioGroup
              row
              value={settings.contentOrder}
              onChange={(e) =>
                updateSettings({ contentOrder: e.target.value as 'description-first' | 'bullets-first' })
              }
            >
              <FormControlLabel value="description-first" control={<Radio size="small" />} label="Önce açıklama" />
              <FormControlLabel value="bullets-first" control={<Radio size="small" />} label="Önce maddeler" />
            </RadioGroup>
          </FormControl>
        )}

        {!simpleMode && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {showColumnControls && (
            <>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Masaüstü Sütun</InputLabel>
                  <Select
                    label="Masaüstü Sütun"
                    value={desktopCols}
                    onChange={(e) => updateSettings({ columnsDesktop: Number(e.target.value) || 3 })}
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n} sütun
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tablet Sütun</InputLabel>
                  <Select
                    label="Tablet Sütun"
                    value={Math.min(4, Math.max(1, Number(settings.columnsTablet) || 2))}
                    onChange={(e) => updateSettings({ columnsTablet: Number(e.target.value) || 2 })}
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n} sütun
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {(layoutTemplate === 'sidebar-left' || layoutTemplate === 'sidebar-right') && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Izgara Sütun</InputLabel>
                <Select
                  label="Izgara Sütun"
                  value={desktopCols}
                  onChange={(e) => updateSettings({ columnsDesktop: Number(e.target.value) || 2 })}
                >
                  {[1, 2, 3].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} sütun
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Boşluk (px)"
              value={settings.gapPx}
              inputProps={{ min: 0, max: 60 }}
              onChange={(e) => updateSettings({ gapPx: Number(e.target.value) || DEFAULT_CONTENT_CARDS_SETTINGS.gapPx })}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Köşe Yuvarlaklığı"
              value={settings.cardRadiusPx}
              inputProps={{ min: 0, max: 40 }}
              onChange={(e) =>
                updateSettings({ cardRadiusPx: Number(e.target.value) || DEFAULT_CONTENT_CARDS_SETTINGS.cardRadiusPx })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Görsel Oranı</InputLabel>
              <Select
                label="Görsel Oranı"
                value={settings.imageAspectRatio}
                onChange={(e) =>
                  updateSettings({
                    imageAspectRatio: e.target.value as ContentCardsDialogState['settings']['imageAspectRatio']
                  })
                }
              >
                <MenuItem value="16/9">16:9</MenuItem>
                <MenuItem value="4/3">4:3</MenuItem>
                <MenuItem value="3/2">3:2</MenuItem>
                <MenuItem value="1/1">1:1 (Kare)</MenuItem>
                <MenuItem value="4/5">4:5 (Dikey promo)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Kart Blok Hizası</InputLabel>
              <Select
                label="Kart Blok Hizası"
                value={settings.gridAlign || 'center'}
                onChange={(e) =>
                  updateSettings({
                    gridAlign: e.target.value as ContentCardsDialogState['settings']['gridAlign']
                  })
                }
              >
                <MenuItem value="left">Sol</MenuItem>
                <MenuItem value="center">Orta</MenuItem>
                <MenuItem value="right">Sağ</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Başlık Hizası (Bölüm + Kart)</InputLabel>
              <Select
                label="Başlık Hizası (Bölüm + Kart)"
                value={settings.titleAlign}
                onChange={(e) =>
                  updateSettings({
                    titleAlign: e.target.value as ContentCardsDialogState['settings']['titleAlign']
                  })
                }
              >
                <MenuItem value="left">Sol</MenuItem>
                <MenuItem value="center">Orta</MenuItem>
                <MenuItem value="right">Sağ</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>
            Kart Başlık Fontları
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Font Ailesi</InputLabel>
                <Select
                  label="Font Ailesi"
                  value={settings.cardTitleStyles?.fontFamily || 'Inter, sans-serif'}
                  onChange={(e) =>
                    updateSettings({
                      cardTitleStyles: {
                        ...settings.cardTitleStyles,
                        fontFamily: e.target.value,
                      },
                    })
                  }
                >
                  {['Inter, sans-serif', 'Arial, sans-serif', 'Poppins, sans-serif', 'Roboto, sans-serif', 'Georgia, serif'].map(
                    (font) => (
                      <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                        {font.split(',')[0]}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Font Boyutu"
                value={settings.cardTitleStyles?.fontSize ?? 18}
                onChange={(e) =>
                  updateSettings({
                    cardTitleStyles: {
                      ...settings.cardTitleStyles,
                      fontSize: Number(e.target.value) || 18,
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Renk"
                value={settings.cardTitleStyles?.color || '#0f172a'}
                onChange={(e) =>
                  updateSettings({
                    cardTitleStyles: {
                      ...settings.cardTitleStyles,
                      color: e.target.value,
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.cardTitleStyles?.bold !== false}
                    onChange={(e) =>
                      updateSettings({
                        cardTitleStyles: {
                          ...settings.cardTitleStyles,
                          bold: e.target.checked,
                        },
                      })
                    }
                  />
                }
                label="Kalın"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.cardTitleStyles?.italic === true}
                    onChange={(e) =>
                      updateSettings({
                        cardTitleStyles: {
                          ...settings.cardTitleStyles,
                          italic: e.target.checked,
                        },
                      })
                    }
                  />
                }
                label="İtalik"
              />
            </Grid>
          </Grid>
        </Grid>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {state.cards.map((card, index) => (
            <Box
              key={`content_card_${index}`}
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: '#fafafa' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.25,
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexShrink: 0 }}>
                  Kart #{index + 1}
                  {(layoutTemplate === 'sidebar-left' || layoutTemplate === 'sidebar-right' || layoutTemplate === 'featured') &&
                    index === 0 && (
                      <Typography component="span" variant="caption" color="primary" sx={{ ml: 1, fontWeight: 600 }}>
                        {layoutTemplate === 'featured' ? '(Öne çıkan)' : '(Yan panel)'}
                      </Typography>
                    )}
                  {(layoutTemplate === 'promo-large-left' || layoutTemplate === 'promo-large-right') && index === 0 && (
                    <Typography component="span" variant="caption" color="primary" sx={{ ml: 1, fontWeight: 600 }}>
                      (Büyük kart)
                    </Typography>
                  )}
                  {(layoutTemplate === 'promo-large-left' || layoutTemplate === 'promo-large-right') &&
                    (index === 1 || index === 2) && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 600 }}>
                        (Küçük kart)
                      </Typography>
                    )}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    ml: 'auto',
                    p: 0.5,
                    pl: 0.75,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 108, width: { xs: '100%', sm: 108 } }}>
                    <InputLabel>Kart Hizası</InputLabel>
                    <Select
                      label="Kart Hizası"
                      value={resolveCardAlign(card, settings.titleAlign || 'left')}
                      onChange={(e) =>
                        updateCard(index, { align: e.target.value as ContentCardAlign })
                      }
                    >
                      <MenuItem value="left">Sol</MenuItem>
                      <MenuItem value="center">Orta</MenuItem>
                      <MenuItem value="right">Sağ</MenuItem>
                    </Select>
                  </FormControl>
                  {showSmallIconField && (
                    <FormControl size="small" sx={{ minWidth: 118, width: { xs: '100%', sm: 118 } }}>
                      <InputLabel>İkon Boyutu</InputLabel>
                      <Select
                        label="İkon Boyutu"
                        value={(card.iconSize === 'sm' || card.iconSize === 'lg' ? card.iconSize : 'md') as ContentCardIconSize}
                        onChange={(e) =>
                          updateCard(index, { iconSize: e.target.value as ContentCardIconSize })
                        }
                      >
                        <MenuItem value="sm">Küçük</MenuItem>
                        <MenuItem value="md">Orta</MenuItem>
                        <MenuItem value="lg">Büyük</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {layoutTemplate === 'custom' && (
                    <FormControl size="small" sx={{ minWidth: 96, width: { xs: '100%', sm: 96 } }}>
                      <InputLabel>Sütun</InputLabel>
                      <Select
                        label="Sütun"
                        value={Math.min(desktopCols, Math.max(1, Number(card.colSpan) || 1))}
                        onChange={(e) => updateCard(index, { colSpan: Number(e.target.value) || 1 })}
                      >
                        {Array.from({ length: desktopCols }, (_, i) => i + 1).map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}/{desktopCols}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {state.cards.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeCard(index)}
                      aria-label="Kartı sil"
                      sx={{
                        width: 36,
                        height: 36,
                        border: '1px solid',
                        borderColor: 'error.light',
                        borderRadius: 1,
                        bgcolor: 'rgba(211,47,47,0.04)',
                        '&:hover': { bgcolor: 'rgba(211,47,47,0.1)' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Grid container spacing={1.5}>
                {(simpleMode || isBanner) && (
                  <>
                    <Grid item xs={12} md={7}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Ürün görseli"
                          placeholder="https://..."
                          value={card.imageUrl}
                          onChange={(e) => updateCard(index, { imageUrl: e.target.value })}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onOpenImageLibrary(index, 'imageUrl')}
                          sx={{ flexShrink: 0, height: 40, whiteSpace: 'nowrap' }}
                        >
                          Görsel Seç
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: 'stretch' }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Marka ikonu / logo"
                          placeholder="https://..."
                          value={card.brandLogoUrl || ''}
                          onChange={(e) => updateCard(index, { brandLogoUrl: e.target.value })}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onOpenImageLibrary(index, 'brandLogoUrl')}
                          sx={{ flexShrink: 0, height: 40, whiteSpace: 'nowrap' }}
                        >
                          Logo Seç
                        </Button>
                      </Box>
                      {card.brandLogoUrl ? (
                        <Box
                          sx={{
                            mt: 0.75,
                            width: 72,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0',
                            backgroundImage: `url(${card.brandLogoUrl})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            px: 0.5
                          }}
                        />
                      ) : null}
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Görsel konumu</InputLabel>
                        <Select
                          label="Görsel konumu"
                          value={(card.imageSide === 'right' ? 'right' : 'left') as ContentCardImageSide}
                          onChange={(e) =>
                            updateCard(index, { imageSide: e.target.value as ContentCardImageSide })
                          }
                        >
                          <MenuItem value="left">Solda</MenuItem>
                          <MenuItem value="right">Sağda</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label="Başlık"
                        placeholder="Örn. Yaz indirimi başladı"
                        value={card.title || ''}
                        onChange={(e) => updateCard(index, { title: e.target.value })}
                        error={!String(card.title || '').trim()}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'stretch',
                          gap: 1,
                          height: '100%'
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label="İndirim yüzdesi"
                          placeholder="15"
                          value={normalizeBannerPercent(card.badge)}
                          onChange={(e) => {
                            const pct = normalizeBannerPercent(e.target.value);
                            updateCard(index, { badge: pct || undefined });
                          }}
                          inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">%</InputAdornment>
                          }}
                          helperText="Kartta büyük görünür"
                        />
                        {normalizeBannerPercent(card.badge) && (
                          <Box
                            sx={{
                              minWidth: { xs: 72, sm: 88 },
                              px: 1,
                              borderRadius: 2,
                              bgcolor: card.panelColor || BANNER_PANEL_COLORS[0],
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 900,
                              fontSize: { xs: '1.55rem', sm: '1.85rem' },
                              lineHeight: 1,
                              letterSpacing: '-0.03em',
                              flexShrink: 0
                            }}
                          >
                            {formatBannerPercentLabel(card.badge)}
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Kampanya tarihi
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.25,
                            alignItems: { xs: 'stretch', sm: 'center' }
                          }}
                        >
                          <DatePicker
                            label="Başlangıç"
                            value={ymdToDate(card.startDate)}
                            onChange={(next) => {
                              const startDate = toYmd(next);
                              const endDate = String(card.endDate || '').trim();
                              updateCard(index, {
                                startDate: startDate || undefined,
                                date:
                                  resolveBannerDateLabel({ startDate, endDate, date: '' }) ||
                                  undefined
                              });
                            }}
                            slotProps={{
                              textField: { size: 'small', fullWidth: true }
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}
                          >
                            —
                          </Typography>
                          <DatePicker
                            label="Bitiş"
                            value={ymdToDate(card.endDate)}
                            minDate={ymdToDate(card.startDate) || undefined}
                            onChange={(next) => {
                              const endDate = toYmd(next);
                              const startDate = String(card.startDate || '').trim();
                              updateCard(index, {
                                endDate: endDate || undefined,
                                date:
                                  resolveBannerDateLabel({ startDate, endDate, date: '' }) ||
                                  undefined
                              });
                            }}
                            slotProps={{
                              textField: { size: 'small', fullWidth: true }
                            }}
                          />
                        </Box>
                      </LocalizationProvider>
                      {resolveBannerDateLabel(card) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                          Kart etiketi: {resolveBannerDateLabel(card)}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Alt yazı / CTA"
                        placeholder="Örn. Ürünleri Keşfet"
                        value={card.description || ''}
                        onChange={(e) => updateCard(index, { description: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link URL"
                        placeholder="/kategori/..."
                        value={card.linkUrl || ''}
                        onChange={(e) => updateCard(index, { linkUrl: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Panel rengi
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        {BANNER_PANEL_COLORS.map((c, colorIdx) => {
                          const selected =
                            String(card.panelColor || BANNER_PANEL_COLORS[index % BANNER_PANEL_COLORS.length]).toLowerCase() ===
                            c.toLowerCase();
                          return (
                            <Box
                              key={`${c}_${colorIdx}`}
                              onClick={() => updateCard(index, { panelColor: c })}
                              role="button"
                              tabIndex={0}
                              aria-label={`Panel rengi ${c}`}
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1,
                                bgcolor: c,
                                cursor: 'pointer',
                                border: selected ? '2px solid #0f172a' : '2px solid #fff',
                                boxShadow: selected
                                  ? '0 0 0 2px rgba(15,23,42,0.25)'
                                  : '0 0 0 1px #e2e8f0'
                              }}
                            />
                          );
                        })}
                        <TextField
                          type="color"
                          size="small"
                          value={card.panelColor || BANNER_PANEL_COLORS[0]}
                          onChange={(e) => updateCard(index, { panelColor: e.target.value })}
                          sx={{
                            width: 42,
                            '& .MuiOutlinedInput-root': { height: 32 },
                            '& input': { cursor: 'pointer', p: 0.2, height: 28 }
                          }}
                        />
                      </Box>
                    </Grid>
                  </>
                )}

                {!simpleMode && !isBanner && showSmallIconField && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Küçük İkon (Emoji veya İkon)
                    </Typography>
                    <ContentCardIconSelect
                      iconKey={card.iconKey}
                      emoji={card.emoji}
                      onIconKeyChange={(nextKey) =>
                        updateCard(index, {
                          iconKey: nextKey || undefined,
                          emoji: undefined,
                          useIcon: Boolean(nextKey)
                        })
                      }
                      onEmojiChange={(nextEmoji) =>
                        updateCard(index, {
                          emoji: nextEmoji,
                          iconKey: undefined,
                          useIcon: Boolean(nextEmoji)
                        })
                      }
                    />
                  </Grid>
                )}
                {!simpleMode && !isBanner && showLargeImageField && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Büyük Görsel
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label={isFeatureSplit ? 'Promo Görsel URL' : 'Görsel URL (Opsiyonel)'}
                        placeholder="https://..."
                        value={card.imageUrl}
                        onChange={(e) => updateCard(index, { imageUrl: e.target.value })}
                        sx={{ flex: 1, minWidth: 0 }}
                        required={isPromoLayout || isFeatureSplit}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onOpenImageLibrary(index)}
                        sx={{
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          height: 40,
                          px: 1.5,
                          alignSelf: { xs: 'stretch', sm: 'center' }
                        }}
                      >
                        Resim Kütüphanesinden Seç
                      </Button>
                    </Box>
                  </Grid>
                )}
                {isFeatureSplit && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Görsel konumu</InputLabel>
                      <Select
                        label="Görsel konumu"
                        value={(card.imageSide === 'left' ? 'left' : 'right') as ContentCardImageSide}
                        onChange={(e) =>
                          updateCard(index, { imageSide: e.target.value as ContentCardImageSide })
                        }
                      >
                        <MenuItem value="right">Sağda</MenuItem>
                        <MenuItem value="left">Solda</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {!simpleMode && !isBanner && (
                <Grid item xs={12} md={isPricing || isFeatureSplit ? 12 : 6}>
                  <TextField
                    fullWidth
                    required
                    label={isPricing ? 'Paket adı' : 'Başlık'}
                    value={card.title || ''}
                    onChange={(e) => updateCard(index, { title: e.target.value })}
                    error={!String(card.title || '').trim()}
                    helperText={
                      !String(card.title || '').trim()
                        ? 'Başlık zorunludur'
                        : isFeatureSplit
                          ? 'Büyük başlık — renk ayarı yukarıdaki Başlık Stili bölümünden'
                          : 'Diğer alanlar opsiyoneldir'
                    }
                  />
                </Grid>
                )}
                {isPricingScroll && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Paket etiketi"
                      placeholder="Örn. YILLIK PAKET"
                      value={card.subtitle || ''}
                      onChange={(e) => updateCard(index, { subtitle: e.target.value })}
                      helperText="Boşsa paket adı kullanılır"
                    />
                  </Grid>
                )}
                {!simpleMode && !isBanner && !isFeatureSplit && !isEmoji && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={isPricing ? 'Popüler rozet metni' : 'Etiket (Tag)'}
                    placeholder={isPricing ? 'Örn. En Popüler' : 'Örn. Perakende Kasa'}
                    helperText={
                      isPricing ? 'Öne çıkan kartta üst rozet olarak görünür' : ' '
                    }
                    value={card.badge || (!isPricing ? card.subtitle : '') || ''}
                    onChange={(e) =>
                      updateCard(index, {
                        badge: e.target.value,
                        ...(!isPricing ? { subtitle: '' } : {})
                      })
                    }
                  />
                </Grid>
                )}
                {isEmoji && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      İkon daire rengi
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        minHeight: 40
                      }}
                    >
                      {DEFAULT_CARD_TAG_COLORS.map((c) => {
                        const selected =
                          String(card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]).toLowerCase() ===
                          c.toLowerCase();
                        return (
                          <Box
                            key={c}
                            onClick={() => updateCard(index, { badgeColor: c })}
                            role="button"
                            tabIndex={0}
                            aria-label={`İkon rengi ${c}`}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                updateCard(index, { badgeColor: c });
                              }
                            }}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: c,
                              cursor: 'pointer',
                              flexShrink: 0,
                              border: selected ? '2px solid #0f172a' : '2px solid #fff',
                              boxShadow: selected
                                ? '0 0 0 2px rgba(15,23,42,0.25)'
                                : '0 0 0 1px #e2e8f0',
                              transition: 'transform 0.12s ease',
                              '&:hover': { transform: 'scale(1.08)' }
                            }}
                          />
                        );
                      })}
                      <TextField
                        type="color"
                        size="small"
                        value={card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]}
                        onChange={(e) => updateCard(index, { badgeColor: e.target.value })}
                        sx={{
                          width: 42,
                          flexShrink: 0,
                          '& .MuiOutlinedInput-root': { height: 32 },
                          '& input': { cursor: 'pointer', p: 0.2, height: 28 }
                        }}
                        inputProps={{ 'aria-label': 'Özel ikon daire rengi' }}
                      />
                    </Box>
                  </Grid>
                )}
                {!simpleMode && !isBanner && !isFeatureSplit && !isPricing && !isEmoji && (
                  <>
                <Grid item xs={12} sm={5} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Etiket Konumu</InputLabel>
                    <Select
                      label="Etiket Konumu"
                      value={(card.badgePosition === 'above' ? 'above' : 'below') as ContentCardBadgePosition}
                      onChange={(e) =>
                        updateCard(index, { badgePosition: e.target.value as ContentCardBadgePosition })
                      }
                    >
                      <MenuItem value="below">Başlığın altında</MenuItem>
                      <MenuItem value="above">Başlığın üstünde</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={7} md={8}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      minHeight: 40
                    }}
                  >
                    {DEFAULT_CARD_TAG_COLORS.map((c) => {
                      const selected =
                        String(card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]).toLowerCase() ===
                        c.toLowerCase();
                      return (
                        <Box
                          key={c}
                          onClick={() => updateCard(index, { badgeColor: c })}
                          role="button"
                          tabIndex={0}
                          aria-label={`Etiket rengi ${c}`}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                              updateCard(index, { badgeColor: c });
                            }
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: c,
                            cursor: 'pointer',
                            flexShrink: 0,
                            border: selected ? '2px solid #0f172a' : '2px solid #fff',
                            boxShadow: selected
                              ? '0 0 0 2px rgba(15,23,42,0.25)'
                              : '0 0 0 1px #e2e8f0',
                            transition: 'transform 0.12s ease',
                            '&:hover': { transform: 'scale(1.08)' }
                          }}
                        />
                      );
                    })}
                    <TextField
                      type="color"
                      size="small"
                      value={card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]}
                      onChange={(e) => updateCard(index, { badgeColor: e.target.value })}
                      sx={{
                        width: 42,
                        flexShrink: 0,
                        '& .MuiOutlinedInput-root': { height: 32 },
                        '& input': { cursor: 'pointer', p: 0.2, height: 28 }
                      }}
                      inputProps={{ 'aria-label': 'Özel etiket rengi' }}
                    />
                  </Box>
                </Grid>
                  </>
                )}
                {isPricing && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Fiyat"
                        placeholder={isPricingTrio ? 'Örn. ₺340' : 'Örn. 50 TL'}
                        value={card.price || ''}
                        onChange={(e) => updateCard(index, { price: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Fiyat eki"
                        placeholder={isPricingTrio ? '/ Aylık' : '/ Kullanıcı / Ay'}
                        value={card.priceSuffix || ''}
                        onChange={(e) => updateCard(index, { priceSuffix: e.target.value })}
                      />
                    </Grid>
                    {isPricingScroll && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Buton metni"
                          placeholder="Ücretsiz Deneyin"
                          value={card.ctaLabel || ''}
                          onChange={(e) => updateCard(index, { ctaLabel: e.target.value })}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(card.featured)}
                            onChange={(e) => updateCard(index, { featured: e.target.checked })}
                          />
                        }
                        label="Öne çıkan / popüler paket"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Kenarlık / vurgu rengi
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          minHeight: 40
                        }}
                      >
                        {DEFAULT_CARD_TAG_COLORS.map((c) => {
                          const selected =
                            String(card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]).toLowerCase() ===
                            c.toLowerCase();
                          return (
                            <Box
                              key={c}
                              onClick={() => updateCard(index, { badgeColor: c })}
                              role="button"
                              tabIndex={0}
                              aria-label={`Vurgu rengi ${c}`}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                  updateCard(index, { badgeColor: c });
                                }
                              }}
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: c,
                                cursor: 'pointer',
                                flexShrink: 0,
                                border: selected ? '2px solid #0f172a' : '2px solid #fff',
                                boxShadow: selected
                                  ? '0 0 0 2px rgba(15,23,42,0.25)'
                                  : '0 0 0 1px #e2e8f0',
                                transition: 'transform 0.12s ease',
                                '&:hover': { transform: 'scale(1.08)' }
                              }}
                            />
                          );
                        })}
                        <TextField
                          type="color"
                          size="small"
                          value={card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]}
                          onChange={(e) => updateCard(index, { badgeColor: e.target.value })}
                          sx={{
                            width: 42,
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': { height: 32 },
                            '& input': { cursor: 'pointer', p: 0.2, height: 28 }
                          }}
                          inputProps={{ 'aria-label': 'Özel vurgu rengi' }}
                        />
                      </Box>
                    </Grid>
                  </>
                )}
                {!simpleMode && !isBanner && (
                  <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={isFeatureSplit ? 2 : 3}
                    label={isPricing ? 'Kısa açıklama' : 'Açıklama'}
                    value={card.description || ''}
                    onChange={(e) => updateCard(index, { description: e.target.value })}
                    helperText={
                      isPricingScroll
                        ? 'Fiyat altındaki not (örn. Yıllık planda avantajlı fiyat.)'
                        : undefined
                    }
                  />
                </Grid>

                {(settings.showBullets || isFeatureSplit || isPricing) && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Maddelendirme
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(card.bulletItems || []).map((bullet, bulletIndex) => (
                        <Box key={`bullet_${index}_${bulletIndex}`} sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder={`Madde ${bulletIndex + 1}`}
                            value={bullet}
                            onChange={(e) => updateBulletItem(index, bulletIndex, e.target.value)}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeBulletItem(index, bulletIndex)}
                            aria-label="Maddeyi sil"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addBulletItem(index)}>
                        Madde Ekle
                      </Button>
                    </Box>
                  </Grid>
                )}

                {settings.showLocation && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Konum"
                      placeholder="Örn. İstanbul"
                      value={card.location || ''}
                      onChange={(e) => updateCard(index, { location: e.target.value })}
                    />
                  </Grid>
                )}

                {settings.showDate && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tarih"
                      placeholder="Örn. 2024"
                      value={card.date || ''}
                      onChange={(e) => updateCard(index, { date: e.target.value })}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Kart Linki (Opsiyonel)"
                    placeholder="https://..."
                    value={card.linkUrl || ''}
                    onChange={(e) => updateCard(index, { linkUrl: e.target.value })}
                  />
                </Grid>
                  </>
                )}
              </Grid>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Canlı Önizleme
          </Typography>
          {layoutTemplate !== 'sidebar-left' && layoutTemplate !== 'sidebar-right' && (
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{ mb: 1.25, textAlign: settings.titleAlign || 'center' }}
            >
              {state.title || 'Bölüm Başlığı (Örnek)'}
            </Typography>
          )}
          {(() => {
            const renderPreviewCard = (card: ContentCardItem, index: number, opts?: { tall?: boolean }) => {
              const cardAlign = resolveCardAlign(card, settings.titleAlign || 'center');
              const tagStyle = resolveTagSoftStyle(card.badgeColor);
              const tagText = String(card.badge || card.subtitle || '').trim();
              const tagNode = tagText ? (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    mt: 0.5,
                    mb: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: tagStyle.bgcolor,
                    color: tagStyle.color,
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    maxWidth: '100%'
                  }}
                >
                  {tagText}
                </Box>
              ) : null;
              const titleNode = (
                <Typography variant="caption" fontWeight={700} display="block">
                  {card.title || 'Başlık'}
                </Typography>
              );
              const titleAndTag =
                card.badgePosition === 'above' ? (
                  <>
                    {tagNode}
                    {titleNode}
                  </>
                ) : (
                  <>
                    {titleNode}
                    {tagNode}
                  </>
                );
              const iconJustify =
                cardAlign === 'right' ? 'flex-end' : cardAlign === 'left' ? 'flex-start' : 'center';
              const iconPx =
                CONTENT_CARD_ICON_SIZE_PX[
                  (card.iconSize === 'sm' || card.iconSize === 'lg' ? card.iconSize : 'md') as ContentCardIconSize
                ] || 64;
              const previewIconPx = Math.min(64, Math.round(iconPx * 0.55));
              const iconBadge =
                card.iconKey || card.emoji ? (
                  <Box sx={{ display: 'flex', justifyContent: iconJustify, mb: 0.75, lineHeight: 0 }}>
                    {card.iconKey ? (
                      <ContentCardPresetIcon iconKey={card.iconKey} size={previewIconPx} compact color="#334155" />
                    ) : (
                      <ContentCardEmojiValue value={card.emoji} size={previewIconPx} />
                    )}
                  </Box>
                ) : null;

              if (variant === 'banner') {
                const panel = String(card.panelColor || BANNER_PANEL_COLORS[index % BANNER_PANEL_COLORS.length]).trim();
                const dateText = resolveBannerDateLabel(card);
                const percentLabel = formatBannerPercentLabel(card.badge);
                const brandLogo = String(card.brandLogoUrl || '').trim();
                const imageOnRight = card.imageSide === 'right';
                return (
                  <Box
                    key={`preview_${index}`}
                    sx={{
                      borderRadius: `${Math.max(20, settings.cardRadiusPx)}px`,
                      overflow: 'hidden',
                      minHeight: opts?.tall ? 128 : 108,
                      height: opts?.tall ? '100%' : 'auto',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      bgcolor: panel,
                      boxShadow: '0 6px 16px rgba(15,23,42,0.08)'
                    }}
                  >
                    <Box
                      sx={{
                        order: imageOnRight ? 2 : 1,
                        p: 0.55,
                        display: 'flex',
                        minWidth: 0
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: `${Math.max(14, Math.round(Math.max(20, settings.cardRadiusPx) * 0.75))}px`,
                          bgcolor: '#fff',
                          overflow: 'hidden',
                          backgroundImage: card.imageUrl ? `url(${card.imageUrl})` : undefined,
                          backgroundSize: 'cover',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center'
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        order: imageOnRight ? 1 : 2,
                        position: 'relative',
                        px: 1.1,
                        py: 1,
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          ...(imageOnRight ? { left: 8 } : { right: 8 }),
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: imageOnRight ? 'flex-start' : 'flex-end',
                          gap: 0.4,
                          maxWidth: '52%'
                        }}
                      >
                        {dateText ? (
                          <Box
                            sx={{
                              px: 0.75,
                              pt: 0.35,
                              pb: 0.25,
                              borderRadius: '0 0 8px 8px',
                              bgcolor: 'rgba(0,0,0,0.18)',
                              fontSize: '0.58rem',
                              fontWeight: 600,
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {dateText}
                          </Box>
                        ) : null}
                        {brandLogo ? (
                          <Box
                            sx={{
                              height: 18,
                              minWidth: 48,
                              maxWidth: '100%',
                              px: 0.6,
                              borderRadius: 0.75,
                              bgcolor: '#fff',
                              backgroundImage: `url(${brandLogo})`,
                              backgroundSize: 'contain',
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'center'
                            }}
                          />
                        ) : null}
                      </Box>
                      {percentLabel && (
                        <Typography
                          sx={{
                            color: '#fff !important',
                            fontWeight: 900,
                            fontSize: opts?.tall ? '1.85rem' : '1.55rem',
                            lineHeight: 0.95,
                            letterSpacing: '-0.04em',
                            mb: 0.2
                          }}
                        >
                          {percentLabel}
                        </Typography>
                      )}
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        sx={{ color: '#fff !important', fontSize: '0.82rem', lineHeight: 1.2 }}
                      >
                        {card.title || 'Başlık'}
                      </Typography>
                      {(card.description || card.linkUrl) && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.92)', mt: 0.25, fontSize: '0.68rem' }}>
                          {card.description || 'Ürünleri Keşfet'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }

              if (variant === 'feature-split') {
                const imageOnRight = card.imageSide !== 'left';
                const bullets = (card.bulletItems || []).filter(Boolean).slice(0, 4);
                const titleColor = settings.cardTitleStyles?.color || '#1e3a8a';
                return (
                  <Box
                    key={`preview_${index}`}
                    sx={{
                      borderRadius: `${settings.cardRadiusPx}px`,
                      border: '1px solid #e8edf3',
                      bgcolor: '#fff',
                      boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                      display: 'grid',
                      gridTemplateColumns: '1.1fr 1fr',
                      gap: 1.25,
                      p: 1.5,
                      alignItems: 'center',
                      minHeight: 140
                    }}
                  >
                    <Box sx={{ order: imageOnRight ? 1 : 2 }}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ color: titleColor, mb: 0.75, lineHeight: 1.25 }}>
                        {card.title || 'Başlık'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
                        {bullets.map((b, bi) => (
                          <Box key={bi} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: '#22c55e',
                                color: '#fff',
                                fontSize: 9,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontWeight: 800
                              }}
                            >
                              ✓
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {b}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        order: imageOnRight ? 2 : 1,
                        borderRadius: 1.5,
                        minHeight: 100,
                        bgcolor: '#e2e8f0',
                        backgroundImage: card.imageUrl ? `url(${card.imageUrl})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        boxShadow: '0 10px 24px rgba(15,23,42,0.12)'
                      }}
                    />
                  </Box>
                );
              }

              if (variant === 'pricing-trio') {
                const anyFeatured = state.cards.some((c) => Boolean(c.featured));
                const isFeatured = anyFeatured ? Boolean(card.featured) : index === 1;
                const accent = String(card.badgeColor || '#1e3a8a').trim() || '#1e3a8a';
                const bullets = (card.bulletItems || []).filter(Boolean).slice(0, 3);
                return (
                  <Box
                    key={`preview_${index}`}
                    sx={{
                      borderRadius: `${settings.cardRadiusPx}px`,
                      border: isFeatured ? `2px solid ${accent}` : '1px solid #e8edf3',
                      bgcolor: '#fff',
                      boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                      p: 1.5,
                      pt: isFeatured ? 2 : 1.5,
                      textAlign: 'center',
                      position: 'relative',
                      minHeight: 160
                    }}
                  >
                    {isFeatured && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          bgcolor: accent,
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          px: 1,
                          py: 0.25,
                          borderRadius: 99,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {card.badge || 'En Popüler'}
                      </Box>
                    )}
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: settings.cardTitleStyles?.color || accent }}>
                      {card.title || 'Paket'}
                    </Typography>
                    {card.description && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35, mb: 0.75 }}>
                        {card.description}
                      </Typography>
                    )}
                    <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: accent, lineHeight: 1.1 }}>
                      {card.price || '—'}
                      {card.priceSuffix && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 500 }}>
                          {card.priceSuffix}
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.35, textAlign: 'left' }}>
                      {bullets.map((b, bi) => (
                        <Box key={bi} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {b}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              }

              if (variant === 'pricing-scroll') {
                const isFeatured = Boolean(card.featured);
                const accent = String(card.badgeColor || '#1e3a8a').trim() || '#1e3a8a';
                const bullets = (card.bulletItems || []).filter(Boolean).slice(0, 3);
                const packageLabel = card.subtitle || card.title || 'Paket';
                return (
                  <Box
                    key={`preview_${index}`}
                    sx={{
                      borderRadius: `${settings.cardRadiusPx}px`,
                      border: isFeatured ? `2px solid ${accent}` : '1px solid #e2e8f0',
                      bgcolor: '#fff',
                      boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                      overflow: 'hidden',
                      minHeight: 180,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {isFeatured && (
                      <Box
                        sx={{
                          bgcolor: accent,
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          textAlign: 'center',
                          py: 0.45
                        }}
                      >
                        ★ {card.badge || 'En Popüler Seçenek'}
                      </Box>
                    )}
                    <Box sx={{ p: 1.25, textAlign: 'center', bgcolor: isFeatured ? `${accent}14` : 'transparent' }}>
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        sx={{ color: settings.cardTitleStyles?.color || accent, letterSpacing: 0.6, textTransform: 'uppercase' }}
                      >
                        {packageLabel}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', mt: 0.35 }}>
                        {card.price || '—'}
                        {card.priceSuffix && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.4 }}>
                            {card.priceSuffix}
                          </Typography>
                        )}
                      </Typography>
                      {card.description && (
                        <Typography variant="caption" sx={{ color: isFeatured ? accent : '#16a34a', fontWeight: 600, display: 'block', mt: 0.25 }}>
                          {card.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ px: 1.1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.4, flex: 1 }}>
                      {bullets.map((b, bi) => (
                        <Box
                          key={bi}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            border: '1px solid #e8edf3',
                            borderRadius: 1,
                            px: 0.7,
                            py: 0.45
                          }}
                        >
                          <Box sx={{ width: 12, height: 12, borderRadius: 0.4, bgcolor: '#22c55e', flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                            {b}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 9 }}>
                            Dahil
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ px: 1.1, pb: 1.1 }}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 0.7,
                          borderRadius: 1,
                          fontSize: 12,
                          fontWeight: 700,
                          bgcolor: isFeatured ? accent : '#fff',
                          color: isFeatured ? '#fff' : accent,
                          border: isFeatured ? 'none' : `1.5px solid ${accent}`
                        }}
                      >
                        {card.ctaLabel || 'Ücretsiz Deneyin'}
                      </Box>
                    </Box>
                  </Box>
                );
              }

              if (variant === 'emoji') {
                const circleColor = String(card.badgeColor || DEFAULT_CARD_TAG_COLORS[0]).trim() || '#2563eb';
                return (
                  <Box
                    key={`preview_${index}`}
                    sx={{
                      borderRadius: `${settings.cardRadiusPx}px`,
                      border: '1px solid #e8edf3',
                      bgcolor: '#fff',
                      boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                      p: 1.5,
                      textAlign: 'center',
                      minHeight: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: circleColor,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                        boxShadow: `0 6px 14px ${circleColor}55`
                      }}
                    >
                      {card.iconKey ? (
                        <ContentCardPresetIcon iconKey={card.iconKey} size={20} compact color="#fff" />
                      ) : card.emoji ? (
                        <ContentCardEmojiValue value={card.emoji} size={20} />
                      ) : (
                        <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.7)' }} />
                      )}
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      sx={{ color: settings.cardTitleStyles?.color || '#0f172a', lineHeight: 1.25, mb: 0.5 }}
                    >
                      {card.title || 'Başlık'}
                    </Typography>
                    {card.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4
                        }}
                      >
                        {card.description}
                      </Typography>
                    )}
                  </Box>
                );
              }

              return (
              <Box
                key={`preview_${index}`}
                sx={{
                  borderRadius: `${settings.cardRadiusPx}px`,
                  border: '1px solid #e5e7eb',
                  bgcolor: '#fff',
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                  minHeight: opts?.tall ? 180 : 100,
                  height: opts?.tall ? '100%' : 'auto',
                  gridColumn: layoutTemplate === 'custom' ? `span ${Math.min(desktopCols, Math.max(1, Number(card.colSpan) || 1))}` : undefined
                }}
              >
                {showLargeImageField && card.imageUrl && (
                  <Box
                    component="img"
                    src={card.imageUrl}
                    alt=""
                    sx={{ width: '100%', height: 64, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ p: 1.1, textAlign: cardAlign }}>
                  {iconBadge}
                  {titleAndTag}
                  {card.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: '-webkit-box', WebkitLineClamp: opts?.tall ? 6 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {card.description}
                    </Typography>
                  )}
                  {opts?.tall && (card.bulletItems || []).filter(Boolean).length > 0 && (
                    <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2, textAlign: 'left' }}>
                      {(card.bulletItems || []).filter(Boolean).slice(0, 5).map((b, i) => (
                        <Typography key={i} component="li" variant="caption" color="text.secondary">
                          {b}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              );
            };

            if (layoutTemplate === 'sidebar-left' || layoutTemplate === 'sidebar-right') {
              const sideCard = state.cards[0];
              const gridCards = state.cards.slice(1);
              const side = sideCard ? renderPreviewCard(sideCard, 0, { tall: true }) : null;
              const grid = (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ mb: 1, textAlign: settings.titleAlign || 'left' }}
                  >
                    {state.title || 'Bölüm Başlığı (Örnek)'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.min(3, desktopCols)}, minmax(0, 1fr))`,
                      gap: 1.25
                    }}
                  >
                    {gridCards.map((card, i) => renderPreviewCard(card, i + 1))}
                  </Box>
                </Box>
              );
              return (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' },
                    gap: 1.5,
                    alignItems: 'stretch',
                    direction: layoutTemplate === 'sidebar-right' ? 'rtl' : 'ltr',
                    '& > *': { direction: 'ltr' }
                  }}
                >
                  {side}
                  {grid}
                </Box>
              );
            }

            if (layoutTemplate === 'promo-trio') {
              return (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.25
                  }}
                >
                  {state.cards.slice(0, 3).map((card, i) => renderPreviewCard(card, i, { tall: true }))}
                </Box>
              );
            }

            if (layoutTemplate === 'promo-large-left' || layoutTemplate === 'promo-large-right') {
              const large = state.cards[0];
              const smalls = state.cards.slice(1, 3);
              const largeNode = large ? renderPreviewCard(large, 0, { tall: true }) : null;
              const smallNode = (
                <Box sx={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 1.25, minHeight: 220 }}>
                  {smalls.map((card, i) => renderPreviewCard(card, i + 1, { tall: true }))}
                </Box>
              );
              return (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.25,
                    alignItems: 'stretch',
                    direction: layoutTemplate === 'promo-large-right' ? 'rtl' : 'ltr',
                    '& > *': { direction: 'ltr' }
                  }}
                >
                  {largeNode}
                  {smallNode}
                </Box>
              );
            }

            if (layoutTemplate === 'featured') {
              const featured = state.cards[0];
              const rest = state.cards.slice(1);
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {featured && renderPreviewCard(featured, 0, { tall: true })}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${desktopCols}, minmax(0, 1fr))`,
                      gap: 1.25
                    }}
                  >
                    {rest.map((card, i) => renderPreviewCard(card, i + 1))}
                  </Box>
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${desktopCols}, minmax(0, 1fr))`,
                  gap: 1.25,
                  width: '100%',
                  maxWidth: '100%',
                  marginLeft: previewAlign === 'right' || previewAlign === 'center' ? 'auto' : 0,
                  marginRight: previewAlign === 'left' || previewAlign === 'center' ? 'auto' : 0
                }}
              >
                {state.cards.map((card, index) => renderPreviewCard(card, index))}
              </Box>
            );
          })()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={addCard} startIcon={<AddIcon />} disabled={state.cards.length >= MAX_CONTENT_CARDS}>
          Kart Ekle {state.cards.length >= MAX_CONTENT_CARDS ? `(Maks. ${MAX_CONTENT_CARDS})` : `(${state.cards.length}/${MAX_CONTENT_CARDS})`}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>İptal</Button>
          <Button variant="contained" onClick={onSave}>
            {state.isEdit ? 'Kaydet' : 'Sayfaya Ekle'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ContentCardsDialog;
